import { CommonModule } from '@angular/common';
import { Component, HostListener, Input, OnDestroy, OnInit } from '@angular/core';
import { CommonService } from '@src/app/services/common.service';
import { GlobalStatusService } from '@src/app/services/global-status.service';
import { RequestService } from '@src/app/services/request.service';
import { WalletStatusService } from '@src/app/services/wallet-status.service';
import { FollowAddressItem } from '@src/app/shared/types/address.type';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { Subject, filter, takeUntil } from 'rxjs';

@Component({
    standalone: true,
    selector: 'app-follow-btn',
    templateUrl: './follow-btn.component.html',
    styleUrls: ['./follow-btn.component.less'],
    imports: [CommonModule, NzButtonModule, NzIconModule],
})
export class FollowBtnComponent implements OnInit, OnDestroy {
    @Input() followedWalletAddressInfo!: Pick<FollowAddressItem, 'is_follower' | 'is_following' | 'wallet_address'> & {
        [key: string]: any;
    };
    @Input() theme: 'light' | 'dark' = 'light';
    @Input() size: 'default' | 'large' = 'default';

    updating = false;

    destroy$ = new Subject();

    get btnStatus(): 'follow' | 'followed' {
        if (!this.followedWalletAddressInfo) return 'follow';
        if (!this.followedWalletAddressInfo.is_following) return 'follow';
        return 'followed';
    }

    get myWalletAddress() {
        return this.walletStatusService.walletAddress;
    }

    get isMe() {
        return this.myWalletAddress?.toLocaleLowerCase() === this.followedWalletAddressInfo?.wallet_address?.toLowerCase();
    }

    constructor(
        private requestService: RequestService,
        private commonService: CommonService,
        private walletStatusService: WalletStatusService,
        private followStatusService: GlobalStatusService
    ) {}

    @HostListener('click', ['$event'])
    onClick(event: Event) {
        event.stopPropagation();
        event.preventDefault();
    }

    ngOnInit(): void {
        this.followStatusService.followStatusChange$
            .pipe(
                filter(item => !!item),
                filter(({ address }) => address?.toLowerCase() === this.followedWalletAddressInfo?.wallet_address?.toLowerCase()),
                takeUntil(this.destroy$)
            )
            .subscribe(({ status }) => (this.followedWalletAddressInfo.is_following = status));
    }

    ngOnDestroy() {
        this.destroy$.next(null);
        this.destroy$.complete();
    }

    toggleFollowStatus() {
        if (this.updating) return;

        this.updating = true;

        if (this.btnStatus === 'followed') {
            this.unFollowUser(this.myWalletAddress, this.followedWalletAddressInfo.wallet_address)
                .then(() => this.followStatusService.updateFollowStatus(false, this.followedWalletAddressInfo.wallet_address))
                .catch(err => {
                    this.followStatusService.updateFollowStatus(true, this.followedWalletAddressInfo.wallet_address);
                    this.commonService.errorMessageByResponse(err);
                })
                .finally(() => (this.updating = false));
            return;
        }

        if (this.btnStatus === 'follow') {
            this.followUser(this.myWalletAddress, this.followedWalletAddressInfo.wallet_address)
                .then(() => this.followStatusService.updateFollowStatus(true, this.followedWalletAddressInfo.wallet_address))
                .catch(err => {
                    this.followStatusService.updateFollowStatus(false, this.followedWalletAddressInfo.wallet_address);
                    this.commonService.errorMessageByResponse(err);
                })
                .finally(() => (this.updating = false));
        }
    }

    /**
     *
     * @param wallet_address
     * @param followed_wallet_address
     * @description wallet_address follow followed_wallet_address
     */
    private followUser(wallet_address: string, followed_wallet_address: string) {
        return this.requestService.sendRequest({
            method: 'POST',
            url: '/v1/socialscan/user/follow',
            data: { wallet_address, followed_wallet_address },
        });
    }

    private unFollowUser(wallet_address: string, followed_wallet_address: string) {
        return this.requestService.sendRequest({
            method: 'POST',
            url: '/v1/socialscan/user/unfollow',
            data: { wallet_address, followed_wallet_address },
        });
    }
}
